import {
  Box,
  Button,
  CircularProgress,
  Grid,
  TextField,
  Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import React, { useMemo, useState } from "react";
import DropdownMenu from "common/DropDownMenu/DropDownMenu";
import FormDialog from "common/Modal/Modal";
import { enqueueSnackbar } from "notistack";
import AlertMessages from "common/Alert/AlertMessages";
import { ServiceProfileService } from "subModule/src/core/services";
import {
  ServiceExcelUpdatePreviewResponse,
  ServiceExcelUpdateResponse,
  ServiceExcelUpdateRow,
} from "subModule/src/services/service/interface";
import { UploadPreviewTable } from "common/Misc/UploadPreviewTable";
import { themes } from "theme/themes";
import { brandingInfo } from "features/server/brandingSlice";
import { useSelector } from "react-redux";
import { selectUserInfo } from "features/user/userSlice";

type ServiceBulkUpdateMenuProps = {
  refetch?: () => void;
  selectedRows?: any[];
  pageRows?: any[];
};

const normalizePreviewRows = (
  data: ServiceExcelUpdatePreviewResponse["data"],
): ServiceExcelUpdateRow[] => {
  if (Array.isArray(data)) return data;
  if (data && typeof data === "object") {
    if (Array.isArray(data.rows)) return data.rows;
    if (Array.isArray(data.content)) return data.content;
  }
  return [];
};

const rowHasErrors = (row: ServiceExcelUpdateRow) => {
  if (Array.isArray(row.invalidValues) && row.invalidValues.length) return true;
  if (Array.isArray(row.errors) && row.errors.length) return true;
  if (typeof row.errors === "string" && row.errors.trim()) return true;
  if (typeof row.error === "string" && row.error.trim()) return true;
  return false;
};

const downloadFailedRowsCsv = (
  rows: ServiceExcelUpdateRow[],
  fileName: string,
) => {
  const failed = rows.filter(rowHasErrors);
  if (!failed.length) return false;

  const headers = [
    "id",
    "serviceCode",
    "title",
    "secondaryTitle",
    "price",
    "errors",
  ];
  const escape = (value: unknown) => {
    const text = String(value ?? "");
    return `"${text.replace(/"/g, '""')}"`;
  };
  const lines = [
    headers.join(","),
    ...failed.map((row) => {
      const errorText =
        (Array.isArray(row.invalidValues)
          ? row.invalidValues
              .map((v) => v.Error || v.error)
              .filter(Boolean)
              .join(" | ")
          : "") ||
        (Array.isArray(row.errors) ? row.errors.join(" | ") : row.errors) ||
        row.error ||
        "";
      return [
        escape(row.id),
        escape(row.serviceCode),
        escape(row.title),
        escape(row.secondaryTitle),
        escape(row.price),
        escape(errorText),
      ].join(",");
    }),
  ];
  const blob = new Blob([lines.join("\n")], {
    type: "text/csv;charset=utf-8;",
  });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.setAttribute("download", fileName);
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
  return true;
};

const ServiceBulkUpdateMenu: React.FC<ServiceBulkUpdateMenuProps> = ({
  refetch,
  selectedRows = [],
  pageRows = [],
}) => {
  const [modal, setModal] = useState(false);
  const { t } = useTranslation();
  const branding = useSelector(brandingInfo);
  const themeColors =
    branding?.attributes?.themeId === 100
      ? branding?.attributes?.customTheme
      : (themes as any)[branding?.attributes?.themeId || 3];
  const loggedInUserData = useSelector(selectUserInfo);
  const primaryLanguage = loggedInUserData?.business?.primaryLanguage;
  const secondaryLanguage = loggedInUserData?.business?.secondaryLanguage;
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [imports, setImports] = useState<ServiceExcelUpdateRow[]>([]);
  const [fileName, setFileName] = useState("");
  const [summary, setSummary] = useState<{
    total: number;
    updated: number;
    failed: number;
  } | null>(null);

  const columns = [
    { id: "id", label: t("id") || "ID", numeric: false },
    { id: "categoryService", label: t("category"), numeric: false },
    { id: "serviceCode", label: t("serviceCode"), numeric: false },
    {
      id: "title",
      label: `${!primaryLanguage ? t("primary") : ""} ${t("title")} ${
        primaryLanguage ? ` (${t(primaryLanguage)})` : ""
      }`,
      numeric: false,
    },
    {
      id: "secondaryTitle",
      label: `${!secondaryLanguage ? t("secondary") : ""} ${t("title")} ${
        secondaryLanguage ? ` (${t(secondaryLanguage)})` : ""
      }`,
      numeric: false,
    },
    { id: "itemGroup", label: t("partType"), numeric: false },
    { id: "itemGroupTitle", label: t("itemGroup"), numeric: false },
    { id: "serviceGroupTitle", label: t("serviceGroup"), numeric: false },
    {
      id: "checklistMandatory",
      label: t("Is checklist mandatory?"),
      numeric: false,
    },
    { id: "checklistTitle", label: t("Checklist"), numeric: false },
    {
      id: "applyToAllStations",
      label: t("applyToAllStations"),
      numeric: false,
    },
    { id: "taxable", label: t("taxable"), numeric: false },
    { id: "taxProcedureTitle", label: t("taxProcedure"), numeric: false },
    { id: "price", label: t("price"), numeric: false },
    { id: "pricingScope", label: t("pricingScope"), numeric: false },
    { id: "pricingType", label: t("pricingType"), numeric: false },
    { id: "timeUnitTitle", label: t("timeUnit"), numeric: false },
    { id: "serviceTime", label: t("Billed Time"), numeric: false },
    { id: "itemLimit", label: t("itemLimit"), numeric: false },
    { id: "minLabourPrice", label: t("minLabourPrice"), numeric: false },
    { id: "serviceEveryMiles", label: t("Mileage (km)"), numeric: false },
    {
      id: "serviceEveryPeriodTime",
      label: t("Number of Days"),
      numeric: false,
    },
  ];

  const previewStats = useMemo(() => {
    const total = imports.length;
    const failed = imports.filter(rowHasErrors).length;
    return { total, failed, valid: total - failed };
  }, [imports]);

  const closeModal = () => {
    setModal(false);
    setFileName("");
    setShowPreview(false);
    setImports([]);
    setSummary(null);
  };

  const downloadSelectedForUpdate = (useAllOnPage = false) => {
    const sourceRows = useAllOnPage ? pageRows : selectedRows;
    if (!sourceRows.length) {
      enqueueSnackbar(<AlertMessages text={t("selectAtLeastOneService")} />, {
        variant: "warning",
      });
      return;
    }
    setDownloading(true);
    const ids = sourceRows.map((row) => row.id).filter(Boolean);
    ServiceProfileService.exportServicesBySelectedIds(ids)
      .then((res: Blob) => {
        const blob = new Blob([res], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", `ServicesForUpdate.xlsx`);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
        enqueueSnackbar(<AlertMessages text={t("downloadedSuccessfully")} />, {
          variant: "success",
        });
      })
      .catch((err) => {
        console.error("===", err);
        enqueueSnackbar(<AlertMessages statusCode={0} />, {
          variant: "error",
        });
      })
      .finally(() => setDownloading(false));
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    setFileName(file ? file.name : "");
  };

  const submitUpdate = () => {
    setLoading(true);
    const total = imports.length;
    ServiceProfileService.updateServicesFromExcel(imports)
      .then((response: ServiceExcelUpdateResponse) => {
        if (response.success) {
          const updated =
            response.totalUpdated ??
            response.totalAdded ??
            (typeof response.data === "object" &&
            response.data &&
            !Array.isArray(response.data)
              ? response.data.updated
              : undefined) ??
            previewStats.valid;
          const failed =
            response.totalFailed ??
            (typeof response.data === "object" &&
            response.data &&
            !Array.isArray(response.data)
              ? response.data.failedCount
              : undefined) ??
            previewStats.failed;
          const updatedCount = Number(updated) || 0;
          const failedCount = Number(failed) || 0;
          setSummary({
            total,
            updated: updatedCount,
            failed: failedCount,
          });
          const failedRows = Array.isArray(response.data)
            ? response.data
            : response.data &&
                typeof response.data === "object" &&
                Array.isArray(response.data.failed)
              ? response.data.failed
              : imports.filter(rowHasErrors);
          if (failedRows.length) setImports(failedRows);
          refetch?.();
          enqueueSnackbar(
            <AlertMessages
              text={`${t("total")}: ${total}, ${t("updated")}: ${updatedCount}, ${t("failed")}: ${failedCount}`}
            />,
            { variant: failedCount ? "warning" : "success" },
          );
          if (!failedCount) closeModal();
        } else {
          enqueueSnackbar(
            <AlertMessages
              text={response?.message || t("Something went wrong")}
            />,
            { variant: "error" },
          );
        }
      })
      .catch((err: any) => {
        console.error("===", err);
        enqueueSnackbar(
          <AlertMessages
            text={err?.message || err?.data?.message || "Something went wrong"}
          />,
          { variant: "error" },
        );
      })
      .finally(() => setLoading(false));
  };

  return (
    <Box>
      <Button
        id="bulk-update-button"
        variant="contained"
        disableElevation
        onClick={(event: React.MouseEvent<HTMLButtonElement>) =>
          setAnchorEl(event.currentTarget)
        }
        endIcon={<KeyboardArrowDownIcon />}
        className={`bg-white font-Saira font-[600] text-[13px] px-4 py-1 cursor-pointer rounded-[6px]`}
        sx={{
          outline: "2px solid",
          color: themeColors?.secondary,
          borderColor: themeColors?.secondary,
          height: 34,
          minHeight: 34,
        }}
      >
        {downloading ? (
          <CircularProgress sx={{ color: themeColors?.secondary }} size={24} />
        ) : (
          t("bulkUpdate")
        )}
      </Button>
      <DropdownMenu
        anchorEl={anchorEl}
        menuItems={[
          {
            label: t("downloadForUpdate"),
            onClick: () => downloadSelectedForUpdate(false),
          },
          {
            label: t("downloadAllOnPageForUpdate"),
            onClick: () => downloadSelectedForUpdate(true),
          },
          {
            label: t("uploadForUpdate"),
            onClick: () => setModal(true),
          },
        ]}
        onClose={() => setAnchorEl(null)}
        hoverColor={themeColors?.secondary}
      />
      {modal && (
        <FormDialog
          open={modal}
          onClose={(event: any, reason: any) => {
            if (event && reason !== "backdropClick" && !loading) closeModal();
          }}
          title={
            (showPreview ? t("updatePreview") : t("uploadForUpdate")) +
            " " +
            t("services")
          }
          createPopup
          hideActions
          fullWidth={showPreview}
          maxWidth={showPreview ? "xl" : "sm"}
          content={
            <Grid
              container
              className="items-center"
              component="form"
              noValidate={false}
              spacing={1}
              onSubmit={(e) => {
                e.preventDefault();
                setLoading(true);
                const data = new FormData(e.target as HTMLFormElement);
                const file = data.get("file") as File;
                ServiceProfileService.readServicesForUpdate(file)
                  .then((response: ServiceExcelUpdatePreviewResponse) => {
                    if (response.success) {
                      const rows = normalizePreviewRows(response.data);
                      setImports(rows);
                      setShowPreview(true);
                      setSummary(null);
                    } else {
                      enqueueSnackbar(
                        <AlertMessages
                          text={response.message || t("Something went wrong")}
                        />,
                        { variant: "error" },
                      );
                    }
                  })
                  .catch((err: any) => {
                    console.error("===", err);
                    enqueueSnackbar(
                      <AlertMessages
                        text={
                          err?.message ||
                          err?.data?.message ||
                          "Something went wrong"
                        }
                      />,
                      { variant: "error" },
                    );
                  })
                  .finally(() => setLoading(false));
              }}
            >
              {!showPreview ? (
                <>
                  <Grid item xs={12} md={8} id="importfile">
                    <input
                      type="file"
                      name="file"
                      required
                      accept=".xls,.xlsx,.csv,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,text/csv"
                      style={{ color: fileName ? "inherit" : "#fff" }}
                      onChange={handleFileChange}
                      title={fileName}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      type="submit"
                      disabled={loading}
                      margin="dense"
                      value={t("Upload")}
                      InputProps={{
                        startAdornment: loading ? (
                          <CircularProgress
                            size={20}
                            sx={{
                              color: themeColors?.primary,
                              position: "absolute",
                              left: "10px",
                            }}
                          />
                        ) : null,
                      }}
                      sx={{
                        "& div": {
                          borderRadius: "8px",
                          border: "0 !important",
                        },
                        ["& input"]: {
                          fontFamily: "'Saira', sans-serif",
                          fontSize: "14px",
                          lineHeight: "28px",
                          fontWeight: 600,
                          color: "#fff",
                          padding: "0.52rem 2.22rem 0.6rem 2.22rem",
                          boxShadow:
                            "0px 3px 1px -2px rgba(0,0,0,0.2),0px 2px 2px 0px rgba(0,0,0,0.14),0px 1px 5px 0px rgba(0,0,0,0.12)",
                          backgroundImage: `linear-gradient(135deg, ${themeColors?.secondary}, ${themeColors?.secondary + "B3"})`,
                          border: "0 !important",
                          borderRadius: "8px",
                          cursor: "pointer",
                          textTransform: "uppercase",
                          height: "25px",
                        },
                        "& .MuiInputBase-root": { padding: 0 },
                      }}
                    />
                  </Grid>
                </>
              ) : null}
              {showPreview ? (
                <>
                  {/* <Grid item xs={12}>
                    <Typography className="font-Saira text-[13px] font-semibold mb-2">
                      {`${t("total")}: ${summary?.total ?? previewStats.total} | ${t("valid")}: ${previewStats.valid} | ${t("failed")}: ${summary?.failed ?? previewStats.failed}`}
                      {summary ? ` | ${t("updated")}: ${summary.updated}` : ""}
                    </Typography>
                  </Grid> */}
                  <Grid item xs={12}>
                    <UploadPreviewTable rows={imports} columns={columns} />
                  </Grid>
                  <Grid
                    item
                    xs={12}
                    id="form"
                    className="flex justify-end gap-2"
                  >
                    <Button
                      className="secondary"
                      style={{ color: themeColors?.primary }}
                      variant="contained"
                      disableElevation
                      onClick={() => closeModal()}
                    >
                      {t("cancel")}
                    </Button>
                    {/* {(summary?.failed ?? previewStats.failed) > 0 ? (
                      <Button
                        variant="outlined"
                        disableElevation
                        onClick={() => {
                          const ok = downloadFailedRowsCsv(
                            imports,
                            "ServicesUpdateErrors.csv",
                          );
                          if (ok) {
                            enqueueSnackbar(
                              <AlertMessages
                                text={
                                  t("errorFile") +
                                  " " +
                                  t("downloaded") +
                                  " " +
                                  t("successfully")
                                }
                              />,
                              { variant: "success" },
                            );
                          }
                        }}
                      >
                        {t("downloadErrorFile")}
                      </Button>
                    ) : null} */}
                    {!summary ? (
                      <Button
                        // disabled={loading || !previewStats.valid}
                        className="primary"
                        style={{ background: themeColors?.primary }}
                        variant="contained"
                        disableElevation
                        onClick={() => submitUpdate()}
                      >
                        {loading && (
                          <CircularProgress
                            size={15}
                            sx={{
                              color: "#fff",
                              position: "absolute",
                              left: "5px",
                            }}
                          />
                        )}
                        {t("submit")}
                      </Button>
                    ) : null}
                  </Grid>
                </>
              ) : null}
            </Grid>
          }
        />
      )}
    </Box>
  );
};

export default ServiceBulkUpdateMenu;
